import type React from 'react';
import { useEffect } from 'react';
import { FormField, FormHelp, Label } from '@zillow/constellation-10';

import type { Section } from '../../../models/index';
import type Field from '../../../models/Field';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';
import { APPLICANT_SCREENING_FEE } from '../../../constants/index';
import { MAX_AMOUNT_ERROR_MESSAGE } from '../../../constants/stringConstants';
import CurrencyInput from '../../Widgets/CurrencyInput';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function ApplicantScreeningFee({ section, setSectionValues }: Props): JSX.Element {
  useEffect(() => {
    section.setCustomFieldValidator(APPLICANT_SCREENING_FEE.FIELD_NAME.AMOUNT, isAmountValid);
  }, []);

  function isAmountValid(amount: string, field?: Field): boolean {
    if (Number(amount.replace(/,/g, '')) > APPLICANT_SCREENING_FEE.BOUNDS.MAX_AMOUNT) {
      (field ?? section.getField(APPLICANT_SCREENING_FEE.FIELD_NAME.AMOUNT)).setErrorMessage(
        MAX_AMOUNT_ERROR_MESSAGE(APPLICANT_SCREENING_FEE.BOUNDS.MAX_AMOUNT),
      );
      return false;
    }
    return true;
  }

  function disableApplicantScreeningFee(): void {
    setSectionValues({
      [APPLICANT_SCREENING_FEE.FIELD_NAME.IS_ENABLED]: false,
      [APPLICANT_SCREENING_FEE.FIELD_NAME.AMOUNT]: '',
    });
  }

  function enableApplicantScreeningFee(): void {
    setSectionValues({
      [APPLICANT_SCREENING_FEE.FIELD_NAME.IS_ENABLED]: true,
      [APPLICANT_SCREENING_FEE.FIELD_NAME.AMOUNT]: '',
    });
  }

  function onAmountChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [APPLICANT_SCREENING_FEE.FIELD_NAME.AMOUNT]: value });
  }

  const applicantScreeningEnabled = section.getFieldValue(APPLICANT_SCREENING_FEE.FIELD_NAME.IS_ENABLED);

  return (
    <FormField
      required
      disabled={section.isFieldDisabled(APPLICANT_SCREENING_FEE.FIELD_NAME.IS_ENABLED)}
      label={<Label>Will you collect an applicant screening fee at the time of signing?</Label>}
      control={
        <YesAndNoExpansion
          yesHandler={enableApplicantScreeningFee}
          noHandler={disableApplicantScreeningFee}
          yesActive={applicantScreeningEnabled}
          disabled={section.isFieldDisabled(APPLICANT_SCREENING_FEE.FIELD_NAME.IS_ENABLED)}
        >
          <FormField
            required
            error={section.getField(APPLICANT_SCREENING_FEE.FIELD_NAME.AMOUNT).hasError}
            label={<Label>Applicant screening fee</Label>}
            disabled={section.isFieldDisabled(APPLICANT_SCREENING_FEE.FIELD_NAME.AMOUNT)}
            control={
              <CurrencyInput
                value={section.getFieldValue(APPLICANT_SCREENING_FEE.FIELD_NAME.AMOUNT)}
                onBlur={onAmountChange}
                onChange={onAmountChange}
                error={section.getField(APPLICANT_SCREENING_FEE.FIELD_NAME.AMOUNT).hasError}
                disabled={section.isFieldDisabled(APPLICANT_SCREENING_FEE.FIELD_NAME.AMOUNT)}
              />
            }
            description={
              section.getField(APPLICANT_SCREENING_FEE.FIELD_NAME.AMOUNT).hasError && (
                <FormHelp>{section.getField(APPLICANT_SCREENING_FEE.FIELD_NAME.AMOUNT).getErrorMessage()}</FormHelp>
              )
            }
          />
        </YesAndNoExpansion>
      }
    />
  );
}
